<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { computed, ref, Ref, watch, watchEffect } from "vue";
import DocumentAlertDO = App.Data.Alerts.DocumentAlertDO;
import { useApiClient } from "@/api/client";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import { t } from "@/plugins/i18n";
import { useInterval } from "@vueuse/core";
import { debounce } from "vue-debounce";
import CheckInterval from "@/modules/alerts/components/CheckInterval.vue";
import { toastApiResult } from "@/toaster";
import formatItemSettings from "../components/formatItemSettings";
import { useBaseStore } from "../../../stores/base";
import DocumentAlertNotifiableDO = App.Data.Alerts.DocumentAlertNotifiableDO;
import AddRecipientToAlert from "@/modules/alerts/components/AddRecipientToAlert.vue";
import PublicUserDO = App.Data.PublicUserDO;
import useDialog from "@/ui/composables/useDialog";
import DeleteAlertDialog from "@/modules/alerts/components/DeleteAlertDialog.vue";

const breadcrumbs = computed(() => [
	{
		title: "Alerts",
		disabled: false,
		to: "/alerts",
	},
	{
		title: alert.value?.storedDocument?.title,
		disabled: true,
		to: "/alerts",
	},
]);

const route = useRoute();
const router = useRouter();
const alert = ref<DocumentAlertDO>();
const availableUsers: Ref<PublicUserDO[]> = ref();

const getAlert = async () => {
	const response = await useApiClient("alerts").getOne(
		parseInt(route.params.id as string)
	);
	if (response.isOk()) {
		alert.value = response.value;
	}
};

const getUsers = async () => {
	const userResponse = await useApiClient("users").getEnvironmentUsers();
	if (userResponse.isOk()) {
		availableUsers.value = userResponse.value;
	}
};

watchEffect(() => {
	getUsers();
	getAlert();
});

const sparklineData = computed(() => {
	return alert.value.checks
		?.map((check) => (check.is_in_alert ? 1 : 0))
		.reverse();
});

const sparklineLabels = computed(() => {
	return alert.value.checks
		?.map((check) => dayjs(check.updated_at).format("HH:mm"))
		.reverse();
});

const headers = [
	{ title: "Checked at", key: "updated_at" },
	{ title: "In alert", key: "is_in_alert", align: "center" },
	{ title: "Message", key: "message" },
];

const itemsPerPage = ref(100);
const isLoading = ref(false);
const items = ref([]);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([{ key: "updated_at", order: "desc" }]);
const ctr = useInterval(5000);
const loadItems = debounce(async () => {
	isLoading.value = true;

	const client = useApiClient("alerts");

	const response = await client.getChecks(alert.value.id, {
		page: page.value,
		itemsPerPage: itemsPerPage.value,
		sortBy: sortBy.value,
	});
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const updateIsActive = async (isActive: boolean) => {
	const oldIsActive = alert.value.is_active;
	alert.value.is_active = isActive;

	const response = await toastApiResult(
		useApiClient("alerts").update(alert.value.id, {
			is_active: isActive,
		})
	);

	if (response.isErr()) {
		alert.value.is_active = oldIsActive;
	}
};

const isAddRecipientDialogVisible: Ref<boolean> = ref(false);

const removeNotifiable = async (
	documentAlertNotifiable: DocumentAlertNotifiableDO
) => {
	const confirm = await useDialog().confirm({
		title: "Delete Alert",
		message: t("alerts.removeRecipientConfirmation"),
		isDelete: true,
	});

	if (!confirm) return false;

	const response = await useApiClient("alerts").deleteNotifiable(
		documentAlertNotifiable
	);
	if (response.isOk()) {
		getAlert();
	}
};

watch(ctr, () => loadItems());

const isDeleteDialogVisible = ref(false);

const removeAlert = () => {
	isDeleteDialogVisible.value = true;
};
</script>

<template>
	<div v-if="alert">
		<delete-alert-dialog
			v-model="isDeleteDialogVisible"
			:alert-id="alert.id"
			:alert-title="alert.storedDocument?.title ?? ''"
			@deleted="router.push({ name: 'alerts.index' })"
		/>
		<add-recipient-to-alert
			v-if="isAddRecipientDialogVisible"
			:alert="alert"
			:configured-notifiables="alert.notifiables"
			:available-users="availableUsers"
			v-model="isAddRecipientDialogVisible"
			@user-added="() => getAlert()"
		/>
		<main-breadcrumbs :breadcrumbs="breadcrumbs" />

		<v-col>
			<v-card>
				<v-card-title class="d-flex gap-4 align-center">
					Alert: {{ alert.storedDocument?.title }}
					<v-spacer />
					<v-label for="isActive" :text="t('alerts.actions.active')"> </v-label>
					<v-checkbox
						id="isActive"
						hide-details
						lab
						:model-value="alert.is_active"
						@update:model-value="(isActive) => updateIsActive(isActive)"
					/>
				</v-card-title>
				<v-divider thickness="1" />
				<v-card-text>
					<div class="d-flex">
						<div class="w-100">
							<div class="pb-2"><strong>Properties:</strong></div>
							<div>
								<strong class="mr-1">Type:</strong>
								<check-interval
									:check-interval-type="alert.check_interval_type"
									:check-interval-specification="
										alert.check_interval_specification
									"
								/>
							</div>
							<div class="pt-2">
								<strong class="mr-1">Settings:</strong>
								{{ formatItemSettings(alert) }}
							</div>
						</div>
						<v-divider class="mx-5" vertical />
						<div class="w-100">
							<div class="pb-2"><strong>Recipients:</strong></div>
							<div class="d-flex py-2 pt-1">
								<div>
									<v-icon icon="fa: fa-regular fa-user" size="18"></v-icon>
								</div>
								<div class="pl-5 w-100 pt-1">
									{{ useBaseStore().currentUser.name }}
									<span class="text-caption">( You )</span>
								</div>
							</div>
							<div class="d-flex pt-0" v-for="notifiable in alert?.notifiables">
								<div class="pt-1">
									<v-icon icon="fa: fa-regular fa-user" size="18"></v-icon>
								</div>
								<div class="pl-5 w-100 pt-2">
									{{ notifiable.notifiable?.name }}
								</div>
								<div class="px-5 pt-1">
									<div v-if="notifiable.is_accepted">
										<v-icon icon="fa: fa-regular fa-check" color="success" />
										<v-tooltip activator="parent">
											User accepted notification
										</v-tooltip>
									</div>
									<div v-else>
										<v-icon
											icon="fa: fa-regular fa-hourglass-start"
											color="warning"
										/>
										<v-tooltip activator="parent">
											Pending user approval
										</v-tooltip>
									</div>
								</div>
								<div>
									<v-btn
										variant="tonal"
										color="error"
										size="small"
										@click="removeNotifiable(notifiable)"
										>Remove</v-btn
									>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-divider />
				<v-card-actions>
					<v-btn
						append-icon="fa: fa-light fa-chevron-right"
						color="primary"
						:to="{
							name: 'explorer.metricWrapper',
							query: {
								'base-document': alert.storedDocument.active_document_uuid,
							},
						}"
						>Open in explorer</v-btn
					>
					<v-spacer />
					<v-btn variant="flat" @click="isAddRecipientDialogVisible = true">
						Add Recipient
					</v-btn>
					<v-btn variant="flat" color="error" @click="removeAlert">
						Delete Alert
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-col>
		<v-col>
			<v-card>
				<v-sheet class="v-sheet--offset mx-auto" color="secondary">
					<v-sparkline
						color="primary"
						line-width="1"
						smooth
						stroke-linecap="round"
						padding="16"
						v-model="sparklineData"
						auto-draw
						:min="0"
						:max="1"
						height="75"
					/>
				</v-sheet>

				<v-card-text class="mt-2 pb-0">
					<v-data-table-server
						v-model:items-per-page="itemsPerPage"
						v-model:page="page"
						v-model:sort-by="sortBy"
						:headers="headers"
						:items="items"
						:items-length="totalItemCount"
						:loading="isLoading"
						@update:options="loadItems()"
					>
						<template v-slot:item.updated_at="{ item }">
							{{ dayjs(item.updated_at).format("DD MMM YYYY HH:mm") }}
						</template>
						<template v-slot:item.is_in_alert="{ item }">
							<div
								class="w-100 d-flex align-center justify-center remove-flex-grow"
							>
								<v-icon
									v-if="item.has_error_occurred"
									icon="fa fa-exclamation-circle"
								/>
								<v-icon
									v-else-if="item.is_in_alert === null"
									icon="fa fa-spin fa-spinner"
								/>
								<v-checkbox-btn
									color="primary"
									v-else
									v-model="item.is_in_alert"
									readonly
								/>
							</div>
						</template>
						<template v-slot:item.message="{ item }">
							<span v-if="item.has_error_occurred">{{
								item.error_message
							}}</span>
							<span v-else>{{ item.message }}</span>
						</template>
					</v-data-table-server>
				</v-card-text>
			</v-card>
		</v-col>
	</div>
</template>

<style lang="scss">
.remove-flex-grow .v-selection-control {
	flex-grow: 0 !important;
}
</style>
